import { toast, Slide, ToastOptions, Id } from "react-toastify";
import { delay } from "./shared/utils";

/**
 * Toast variant types
 */
export type ToastVariant = "success" | "error" | "info" | "warning";

/**
 * Default toast configuration options
 * Applied to all toast notifications unless overridden
 */
export const defaultToastOptions: ToastOptions = {
    autoClose: 8000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    transition: Slide,
    theme: "light",
};

/**
 * Shows an error toast notification
 * @param message - Error message to display
 * @example toastOneError("Failed to save data")
 */
export function toastOneError(message: string): void {
    toast.error(message, defaultToastOptions);
}

/**
 * Shows an info toast notification
 * @param message - Info message to display
 * @example toastOneInfo("Data loaded successfully")
 */
export function toastOneInfo(message: string): void {
    toast.info(message, defaultToastOptions);
}

/**
 * Shows a warning toast notification
 * @param message - Warning message to display
 * @example toastOneWarn("This action cannot be undone")
 */
export function toastOneWarn(message: string): void {
    toast.warn(message, defaultToastOptions);
}

/**
 * Shows a success toast notification
 * @param message - Success message to display
 * @example toastOneSuccess("Profile updated successfully")
 */
export function toastOneSuccess(message: string): void {
    toast.success(message, defaultToastOptions);
}

/**
 * Shows multiple error toasts sequentially with a delay
 * @param messages - Array of error messages to display
 * @example await toastMultipleErrors(["Error 1", "Error 2"])
 */
export async function toastMultipleErrors(messages: string[]): Promise<void> {
    for (const message of messages) {
        await delay(300);
        toastOneError(message);
    }
}

/**
 * Shows multiple warning toasts sequentially with a delay
 * @param messages - Array of warning messages to display
 * @example await toastMultipleWarns(["Warning 1", "Warning 2"])
 */
export async function toastMultipleWarns(messages: string[]): Promise<void> {
    for (const message of messages) {
        await delay(300);
        toastOneWarn(message);
    }
}

/**
 * Updates an existing toast to show success
 * @param successMessage - Success message to display
 * @param id - Toast ID to update
 * @example updateSuccessToast("Save completed", toastId)
 */
export function updateSuccessToast(successMessage: string, id: Id): void {
    toast.update(id, {
        render: successMessage,
        type: "success",
        isLoading: false,
        ...defaultToastOptions,
        autoClose: 3000,
    });
}

/**
 * Updates an existing toast to show failure
 * @param failureMessage - Error message to display
 * @param id - Toast ID to update
 * @example updateFailureToast("Save failed", toastId)
 */
export function updateFailureToast(failureMessage: string, id: Id): void {
    toast.update(id, {
        render: failureMessage,
        type: "error",
        isLoading: false,
        ...defaultToastOptions,
        autoClose: 10000,
    });
}

/**
 * Handles unhandled errors with a generic error message
 * If toast ID is provided, updates existing toast; otherwise creates new error toast
 * @param id - Optional toast ID to update
 * @example handleUnhandledError(toastId)
 */
export function handleUnhandledError(id?: Id): void {
    const message = "Am intampinat o eroare necunoscuta.";
    if (id) return updateFailureToast(message, id);
    return toastOneError(message);
}
